// compra.component.ts
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReportService } from './../../services/report.service';

@Component({
  selector: 'app-compra',
  templateUrl: './compra.component.html',
  styleUrls: ['./compra.component.css']
})
export class CompraComponent implements OnInit {
  reportForm: FormGroup;
  reportData: any[] = [];
  resumen: any = null;
  estadisticas: any = null;
  loading: boolean = false;
  error: string = '';

  // Opciones para el select de fuente
  fuenteOptions = [
    { value: '', label: 'Todas las fuentes' }, // Opción para mostrar todas
    { value: 'unidrogas', label: 'Unidrogas' },
    { value: 'dropopular', label: 'Dropopular' }
  ];

  constructor(
    private fb: FormBuilder,
    private reportService: ReportService
  ) {
    this.reportForm = this.fb.group({
      fecha_inicio: ['', Validators.required],
      fecha_fin: ['', Validators.required],
      fuente: [''], // Cambiado a vacío para "todas las fuentes" por defecto
      incluir_detalle: [true]
    });
  }

  ngOnInit() {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

    this.reportForm.patchValue({
      fecha_inicio: this.formatDate(lastMonth),
      fecha_fin: this.formatDate(today)
    });
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  onSubmit(): void {
    if (this.reportForm.valid) {
      this.loading = true;
      this.error = '';

      const params: any = this.reportForm.value;

      this.reportService.getCompraReport(params).subscribe({
        next: (response: any) => {
          this.resumen = response.resumen || null;
          this.estadisticas = null; // Compra no tiene estadisticas_unidrogas
          this.reportData = response.detalle || [];
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al cargar reporte:', error);
          this.error = 'Error al cargar el reporte. Por favor, intente nuevamente.';
          this.loading = false;
        }
      });
    }
  }

  exportToExcel(): void {
    if (this.reportData.length > 0) {
      const dataForExcel = this.reportData.map(item => ({
        'Fecha': item.fecha || 'N/A',
        'Fuente': item.fuente || 'N/A',
        'Factura': item.facturadespacho || 'N/A',
        'CC Cliente': item.CC_cliente || 'N/A',
        'Cliente': item.nombre_cliente || 'N/A',
        'Médico': item.nombremedico || 'N/A',
        'Especialidad': item.espacialidad_medico || 'N/A',
        'Producto': item.nombrecomercial || 'N/A',
        'Código Producto': item.codigoproducto || 'N/A',
        'Laboratorio': item.laboratorio || 'N/A',
        'Cantidad': item.cantidad || 'N/A',
        // 'Unidades': item.unidad || 'N/A',
        'Valor': item.valor ? this.formatCurrency(item.valor) : 'N/A',
        'Ciudad': item.ciudad || 'N/A',
        'Sucursal': item.sucursal || 'N/A',
        'Empleado': item.empleado || 'N/A',
        'Tipo Venta': item.tipoventa || 'N/A',
        'Médico Registrado': item.medico_encontrado ? 'Sí' : 'No',
        'Paciente Registrado': item.paciente_encontrado ? 'Sí' : 'No',
        'Producto Registrado': item.producto_encontrado ? 'Sí' : 'No',
        'Compra Relacionada': item.compra_relacionada ? 'Sí' : 'No',
        'Producto Info': (item.producto_info && item.producto_info.nombre) ? item.producto_info.nombre : 'N/A',
        'EAN Code': (item.producto_info && item.producto_info.ean_code) ? item.producto_info.ean_code : 'N/A'
      }));

      this.reportService.exportToExcel(dataForExcel, 'reporte_compra');
    }
  }

  exportResumenToExcel(): void {
    if (this.resumen) {
      const resumenData = [{
        'Total Prescripciones': this.resumen.total_prescripciones,
        'Médicos Registrados': this.resumen.prescripciones_con_medico_registrado,
        'Pacientes Registrados': this.resumen.prescripciones_con_paciente_registrado,
        'Productos Registrados': this.resumen.prescripciones_con_producto_registrado,
        'Compras Relacionadas': this.resumen.prescripciones_con_compra_relacionada,
        '% Coincidencia Médicos': this.resumen.porcentaje_coincidencia_medicos,
        '% Coincidencia Pacientes': this.resumen.porcentaje_coincidencia_pacientes,
        '% Coincidencia Productos': this.resumen.porcentaje_coincidencia_productos,
        '% Coincidencia Compras': this.resumen.porcentaje_coincidencia_compras
      }];

      this.reportService.exportToExcel(resumenData, 'resumen_compra');
    }
  }

  clearResults(): void {
    this.reportData = [];
    this.resumen = null;
    this.estadisticas = null;
    this.error = '';
  }

  trackByFn(index: number, item: any): any {
    return item.facturadespacho + '_' + item.codigoproducto || index;
  }

  formatCurrency(value: string | number): string {
    if (value == null || value === undefined || value === '') {
      return '$0';
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) {
      return '$0';
    }
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP'
    }).format(num);
  }

  formatPercentage(value: number): string {
    if (value == null || value === undefined || isNaN(value)) {
      return '0.00%';
    }
    return value.toFixed(2) + '%';
  }

  // Métodos para análisis adicional
  getTotalVentas(): number {
    return this.reportData.reduce((total, item) => {
      return total + parseFloat(item.valor || '0');
    }, 0);
  }

  getVentasPorCiudad(): { [key: string]: number } {
    return this.reportData.reduce((acc, item) => {
      acc[item.ciudad] = (acc[item.ciudad] || 0) + parseFloat(item.valor || '0');
      return acc;
    }, {} as { [key: string]: number });
  }

  getDoctoresConPlanificacion(): any[] {
    return this.reportData.filter(item => item.planificacion_encontrada);
  }
}
